import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
  Chip,
  keyframes,
  Fade,
  Tooltip,
  Card,
  Button,
  Drawer,
  useMediaQuery,
  Rating,
  Badge,
} from '@mui/material';
import {
  Send,
  ArrowBack,
  SmartToy,
  Person,
  Add,
  ChatBubbleOutline,
  Circle,
  Menu as MenuIcon,
  Close,
  AttachFile,
  Image as ImageIcon,
  Star,
  Gavel,
  LocationOn,
  Delete,
  ContentCopy,
  Check,
} from '@mui/icons-material';
import clientService from '../../services/clientService';
import { toast } from 'react-toastify';
import { axelionColors } from '../../theme/axelionTheme';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const AIChatPageGlass = () => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const isMobile = useMediaQuery('(max-width:767px)');
  const isTablet = useMediaQuery('(max-width:1023px)');

  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [matchedLawyers, setMatchedLawyers] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [lawyersPanelOpen, setLawyersPanelOpen] = useState(false);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);

  const handleCopyMessage = (text, index) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedMessageIndex(index);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadConversations();
  }, []);

  // Load matching lawyers when category changes
  useEffect(() => {
    if (currentCategory) {
      loadMatchingLawyers(currentCategory);
    }
  }, [currentCategory]);

  const loadConversations = async () => {
    try {
      const data = await clientService.aiChat.getConversations();
      setConversations(Array.isArray(data) ? data : []);
      if (data.length > 0 && !currentConversationId) {
        loadConversationHistory(data[0].id);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const loadConversationHistory = async (conversationId) => {
    setIsLoadingHistory(true);
    setCurrentConversationId(conversationId);
    try {
      const history = await clientService.aiChat.getChatHistory(conversationId);
      setMessages(Array.isArray(history) ? history : []);
      // Check last AI message for category
      const lastAi = [...(Array.isArray(history) ? history : [])].reverse().find(m => !m.isUser && m.category);
      if (lastAi?.category) {
        setCurrentCategory(lastAi.category);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadMatchingLawyers = async (category) => {
    try {
      // First try exact specialization match
      const data = await clientService.lawyers.searchLawyers({ specialization: category });
      let lawyers = Array.isArray(data) ? data : (data.lawyers || []);

      // If too few results, also load all lawyers sorted by rating
      if (lawyers.length < 3) {
        const allData = await clientService.lawyers.searchLawyers({ sortBy: 'rating' });
        const allLawyers = Array.isArray(allData) ? allData : (allData.lawyers || []);
        const existingIds = new Set(lawyers.map(l => l.id));
        const additional = allLawyers.filter(l => !existingIds.has(l.id));
        lawyers = [...lawyers, ...additional].slice(0, 5);
      }

      // Flatten lawyer data
      setMatchedLawyers(lawyers.map(l => ({
        id: l.id,
        name: l.name,
        specialization: l.profile?.specialization || l.specialization,
        rating: l.profile?.rating || l.rating || 0,
        reviewsCount: l.profile?.reviewsCount || l.reviewsCount || 0,
        price: l.profile?.price || l.price || 0,
        experience: l.profile?.experience || l.experience || 0,
        location: l.profile?.location || l.location || '',
        avatar: l.avatar,
        isExactMatch: (l.profile?.specialization || l.specialization) === category,
      })));
      if (!isMobile && lawyers.length > 0) {
        setLawyersPanelOpen(true);
      }
    } catch (error) {
      console.error('Error loading lawyers:', error);
      setMatchedLawyers([]);
    }
  };

  const startNewConversation = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setInputMessage('');
    setCurrentCategory(null);
    setMatchedLawyers([]);
    setLawyersPanelOpen(false);
  };

  const handleCategoryClick = (category) => {
    const prompts = {
      'Гражданское право': 'У меня вопрос по гражданскому праву. Какие основные права я имею как гражданин Узбекистана?',
      'Семейное право': 'У меня вопрос по семейному праву. Расскажите о порядке расторжения брака в Узбекистане.',
      'Трудовое право': 'У меня вопрос по трудовому праву. Какие мои права как работника в Узбекистане?',
      'Уголовное право': 'У меня вопрос по уголовному праву. Какие права имеет обвиняемый в Узбекистане?',
      'Корпоративное право': 'У меня вопрос по корпоративному праву. Как зарегистрировать ООО в Узбекистане?',
      'Земельное право': 'У меня вопрос по земельному праву. Какие документы нужны для покупки квартиры в Узбекистане?',
    };
    setInputMessage(prompts[category] || `У меня вопрос по теме: ${category}`);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(f => {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} превышает 10MB`);
        return false;
      }
      return true;
    });
    setAttachedFiles(prev => [...prev, ...validFiles]);
    event.target.value = '';
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if ((!inputMessage.trim() && attachedFiles.length === 0) || isLoading) return;

    const fileNames = attachedFiles.map(f => f.name);
    const msgText = inputMessage.trim() + (fileNames.length > 0 ? `\n\n📎 Прикреплено: ${fileNames.join(', ')}` : '');

    const userMessage = {
      text: msgText,
      isUser: true,
      timestamp: new Date().toISOString(),
      files: fileNames,
    };

    setMessages(prev => [...prev, userMessage]);
    const sentMessage = inputMessage;
    const filesToSend = [...attachedFiles];
    setInputMessage('');
    setAttachedFiles([]);
    setIsLoading(true);

    try {
      const response = await clientService.aiChat.sendMessage(sentMessage, currentConversationId, filesToSend);

      const aiMessage = {
        text: response.message || response.reply,
        isUser: false,
        timestamp: new Date().toISOString(),
        category: response.category,
      };

      setMessages(prev => [...prev, aiMessage]);

      if (response.category) {
        setCurrentCategory(response.category);
      }

      if (response.conversationId && !currentConversationId) {
        setCurrentConversationId(response.conversationId);
        loadConversations();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        text: 'Извините, произошла ошибка. Пожалуйста, попробуйте еще раз.',
        isUser: false,
        timestamp: new Date().toISOString(),
        isError: true,
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('uz-UZ').format(price) + ' сум';
  };

  // --- Conversations sidebar content ---
  const ConversationsList = ({ onClose }) => (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {onClose && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.85rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: axelionColors.textDark }}>
            Разговоры
          </Typography>
          <IconButton onClick={onClose}><Close sx={{ color: axelionColors.textDark }} /></IconButton>
        </Box>
      )}

      <Button
        variant="contained" fullWidth startIcon={<Add />}
        onClick={() => { startNewConversation(); onClose?.(); }}
        sx={{
          background: axelionColors.gold, color: '#FFFFFF', textTransform: 'uppercase',
          letterSpacing: '0.1em', fontWeight: 500, borderRadius: '8px', py: 1.5, mb: 2,
          boxShadow: 'none', '&:hover': { background: axelionColors.goldDark, boxShadow: 'none' },
        }}
      >
        Новый разговор
      </Button>

      <Divider sx={{ borderColor: axelionColors.borderLight, mb: 2 }} />

      <Typography variant="subtitle2" sx={{ color: axelionColors.textMuted, mb: 1, px: 1, textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.1em' }}>
        История разговоров
      </Typography>

      <List sx={{ flexGrow: 1, overflowY: 'auto', px: 0 }}>
        {conversations.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: axelionColors.textMuted }}>
            <ChatBubbleOutline sx={{ fontSize: 48, mb: 1, color: axelionColors.borderLight }} />
            <Typography variant="body2">Нет сохраненных разговоров</Typography>
          </Box>
        ) : (
          conversations.map((conv) => (
            <ListItem key={conv.id} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => { loadConversationHistory(conv.id); onClose?.(); }}
                selected={currentConversationId === conv.id}
                sx={{
                  borderRadius: '8px',
                  '&.Mui-selected': { background: axelionColors.accentLight, borderLeft: `3px solid ${axelionColors.gold}`, '&:hover': { background: axelionColors.bgWarm } },
                  '&:hover': { background: axelionColors.bgCream },
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ background: axelionColors.gold, width: 36, height: 36 }}>
                    <ChatBubbleOutline sx={{ fontSize: 20 }} />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={<Typography variant="body2" sx={{ fontWeight: 500, color: axelionColors.textDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conv.title || 'Новый разговор'}</Typography>}
                  secondary={<Typography variant="caption" sx={{ color: axelionColors.textMuted }}>{new Date(conv.updatedAt || conv.createdAt).toLocaleDateString('ru-RU')}</Typography>}
                />
              </ListItemButton>
            </ListItem>
          ))
        )}
      </List>
    </Box>
  );

  // --- Lawyers panel content ---
  const LawyersPanel = ({ onClose }) => (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 500, fontSize: '0.85rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: axelionColors.textDark }}>
            Подходящие юристы
          </Typography>
          {currentCategory && (
            <Chip label={currentCategory} size="small" sx={{ mt: 0.5, bgcolor: axelionColors.accentLight, color: axelionColors.gold, fontWeight: 500, fontSize: '0.7rem' }} />
          )}
        </Box>
        {onClose && <IconButton onClick={onClose}><Close sx={{ color: axelionColors.textDark }} /></IconButton>}
      </Box>

      <Divider sx={{ borderColor: axelionColors.borderLight, mb: 2 }} />

      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {matchedLawyers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: axelionColors.textMuted }}>
            <Gavel sx={{ fontSize: 48, mb: 1, color: axelionColors.borderLight }} />
            <Typography variant="body2">Задайте вопрос, и мы подберём подходящих юристов</Typography>
          </Box>
        ) : (
          matchedLawyers.map((lawyer) => (
            <Card
              key={lawyer.id}
              onClick={() => navigate(`/lawyers/${lawyer.id}`)}
              sx={{
                mb: 2, p: 2, cursor: 'pointer',
                bgcolor: axelionColors.bgWarm,
                border: `1px solid ${axelionColors.borderLight}`,
                borderRadius: '8px',
                transition: 'all 0.2s',
                '&:hover': { borderColor: axelionColors.gold, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
              }}
            >
              {lawyer.isExactMatch && (
                <Chip label="Рекомендован" size="small" sx={{
                  mb: 1, bgcolor: axelionColors.gold, color: '#FFF',
                  fontSize: '0.65rem', fontWeight: 600, height: 20,
                }} />
              )}
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
                <Avatar sx={{ bgcolor: axelionColors.bgBeige, color: axelionColors.gold, border: `2px solid ${lawyer.isExactMatch ? axelionColors.gold : axelionColors.borderLight}`, width: 44, height: 44, fontWeight: 300 }}>
                  {lawyer.name?.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 500, fontSize: '0.85rem', color: axelionColors.textDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {lawyer.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: axelionColors.textMuted }}>
                    {lawyer.specialization}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Star sx={{ fontSize: 16, color: axelionColors.gold }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: axelionColors.textDark }}>{lawyer.rating}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: axelionColors.textMuted }}>({lawyer.reviewsCount} отзывов)</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.7rem', color: axelionColors.textMuted }}>
                  {lawyer.experience} лет опыта
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: axelionColors.gold }}>
                  {formatPrice(lawyer.price)}
                </Typography>
              </Box>

              <Button
                fullWidth size="small"
                onClick={(e) => { e.stopPropagation(); navigate(`/lawyers/${lawyer.id}`); }}
                sx={{
                  mt: 1.5, bgcolor: axelionColors.gold, color: '#FFF',
                  textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500,
                  fontSize: '0.7rem', borderRadius: '8px', py: 0.75, boxShadow: 'none',
                  '&:hover': { bgcolor: axelionColors.goldDark, boxShadow: 'none' },
                }}
              >
                Записаться
              </Button>
            </Card>
          ))
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: axelionColors.bgCream, position: 'relative', overflow: 'hidden' }}>
      <Container maxWidth="xl" sx={{ py: { xs: 1, md: 2 }, px: { xs: 1, md: 2 } }}>
        {/* Header */}
        <Card sx={{
          mb: { xs: 1, md: 2 }, background: axelionColors.bgLight,
          border: `1px solid ${axelionColors.borderLight}`, boxShadow: '0 4px 16px rgba(26,26,26,0.06)',
          borderRadius: '8px', p: { xs: 1.5, md: 2 },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
            <IconButton onClick={() => navigate('/dashboard')} sx={{ color: axelionColors.textDark }}>
              <ArrowBack />
            </IconButton>

            <Avatar sx={{ background: axelionColors.gold, width: { xs: 36, md: 44 }, height: { xs: 36, md: 44 } }}>
              <SmartToy />
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 300, color: axelionColors.textDark, letterSpacing: '0.05em', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                AI Консультант
              </Typography>
              {!isMobile && (
                <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                  Юридическая помощь с подбором специалистов
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {matchedLawyers.length > 0 && (
                <Tooltip title="Подходящие юристы">
                  <IconButton
                    onClick={() => isMobile ? setLawyersPanelOpen(true) : setLawyersPanelOpen(!lawyersPanelOpen)}
                    sx={{ border: `1px solid ${axelionColors.borderLight}`, borderRadius: '8px', color: axelionColors.gold }}
                  >
                    <Badge badgeContent={matchedLawyers.length} sx={{ '& .MuiBadge-badge': { bgcolor: axelionColors.gold, color: '#FFF', fontSize: '0.65rem' } }}>
                      <Gavel />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}

              {isMobile && (
                <IconButton onClick={() => setSidebarOpen(true)} sx={{ border: `1px solid ${axelionColors.borderLight}`, borderRadius: '8px', color: axelionColors.textDark }}>
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        </Card>

        {/* Mobile Drawers */}
        <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)} PaperProps={{ sx: { width: 300, bgcolor: axelionColors.bgLight } }}>
          <ConversationsList onClose={() => setSidebarOpen(false)} />
        </Drawer>

        <Drawer anchor="right" open={lawyersPanelOpen && (isMobile || isTablet)} onClose={() => setLawyersPanelOpen(false)} PaperProps={{ sx: { width: 320, bgcolor: axelionColors.bgLight } }}>
          <LawyersPanel onClose={() => setLawyersPanelOpen(false)} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ display: 'flex', gap: 2, height: { xs: 'calc(100vh - 120px)', md: 'calc(100vh - 160px)' } }}>
          {/* Left: Conversations - Desktop only */}
          {!isMobile && !isTablet && (
            <Card sx={{
              width: 300, minWidth: 280, flexShrink: 0, display: 'flex', flexDirection: 'column',
              background: axelionColors.bgLight, border: `1px solid ${axelionColors.borderLight}`,
              boxShadow: '0 4px 16px rgba(26,26,26,0.06)', borderRadius: '8px', overflow: 'hidden',
            }}>
              <ConversationsList />
            </Card>
          )}

          {/* Center: Chat */}
          <Card sx={{
            flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
            background: axelionColors.bgLight, border: `1px solid ${axelionColors.borderLight}`,
            boxShadow: '0 4px 16px rgba(26,26,26,0.06)', borderRadius: '8px', overflow: 'hidden',
          }}>
            {/* Messages */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column' }}>
              {isLoadingHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress sx={{ color: axelionColors.gold }} />
                </Box>
              ) : messages.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
                  <SmartToy sx={{ fontSize: 64, color: axelionColors.gold, mb: 2 }} />
                  <Typography sx={{ fontWeight: 300, color: axelionColors.textDark, letterSpacing: '0.05em', fontSize: { xs: '1.1rem', md: '1.4rem' } }} gutterBottom>
                    AI Юридический Консультант
                  </Typography>
                  <Typography sx={{ color: axelionColors.textMuted, mb: 3, maxWidth: 450, fontSize: '0.9rem' }}>
                    Задайте вопрос — я проконсультирую и подберу подходящих юристов
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                    {['Гражданское право', 'Семейное право', 'Трудовое право', 'Уголовное право', 'Корпоративное право', 'Земельное право'].map((cat) => (
                      <Chip
                        key={cat} label={cat} clickable
                        onClick={() => handleCategoryClick(cat)}
                        sx={{
                          background: axelionColors.bgWarm, color: axelionColors.textDark,
                          border: `1px solid ${axelionColors.borderLight}`, fontWeight: 500,
                          cursor: 'pointer', transition: 'all 0.2s',
                          '&:hover': { borderColor: axelionColors.gold, background: axelionColors.accentLight, color: axelionColors.gold },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : (
                <>
                  {messages.map((msg, index) => (
                    <Fade in={true} key={index} timeout={500}>
                      <Box sx={{ display: 'flex', justifyContent: msg.isUser ? 'flex-end' : 'flex-start', mb: 2, animation: msg.isUser ? `${slideInLeft} 0.3s ease-out` : `${slideInRight} 0.3s ease-out` }}>
                        {!msg.isUser && (
                          <Avatar sx={{ background: axelionColors.gold, mr: 1, mt: 0.5, width: 36, height: 36 }}>
                            <SmartToy sx={{ fontSize: 20 }} />
                          </Avatar>
                        )}

                        <Box sx={{ maxWidth: { xs: '85%', md: '70%' }, display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{
                            background: msg.isUser ? axelionColors.gold : msg.isError ? axelionColors.errorLight : axelionColors.bgWarm,
                            border: msg.isUser ? 'none' : msg.isError ? `1px solid ${axelionColors.error}20` : `1px solid ${axelionColors.borderLight}`,
                            borderRadius: msg.isUser ? '8px 8px 4px 8px' : '8px 8px 8px 4px',
                            p: 2, boxShadow: '0 2px 4px rgba(26,26,26,0.04)',
                          }}>
                            <Typography sx={{
                              color: msg.isUser ? '#FFFFFF' : msg.isError ? axelionColors.error : axelionColors.textDark,
                              whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.9rem', lineHeight: 1.6,
                            }}>
                              {msg.text}
                            </Typography>

                            {msg.category && (
                              <Chip label={msg.category} size="small" sx={{
                                mt: 1, background: axelionColors.accentLight, color: axelionColors.gold,
                                border: `1px solid ${axelionColors.borderLight}`, fontSize: '0.7rem', fontWeight: 500,
                              }} />
                            )}
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, px: 1, justifyContent: msg.isUser ? 'flex-end' : 'flex-start' }}>
                            <Typography variant="caption" sx={{ color: axelionColors.textMuted }}>
                              {formatTime(msg.timestamp || msg.createdAt)}
                            </Typography>
                            {!msg.isUser && !msg.isError && (
                              <Tooltip title={copiedMessageIndex === index ? 'Скопировано!' : 'Копировать'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyMessage(msg.text, index)}
                                  sx={{
                                    opacity: 0.5,
                                    p: 0.3,
                                    '&:hover': { opacity: 1, color: axelionColors.gold },
                                    color: copiedMessageIndex === index ? axelionColors.success : axelionColors.textMuted,
                                  }}
                                >
                                  {copiedMessageIndex === index ? <Check sx={{ fontSize: 14 }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>

                        {msg.isUser && (
                          <Avatar sx={{ background: axelionColors.textMuted, ml: 1, mt: 0.5, width: 36, height: 36 }}>
                            <Person sx={{ fontSize: 20 }} />
                          </Avatar>
                        )}
                      </Box>
                    </Fade>
                  ))}

                  {isLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ background: axelionColors.gold, mr: 1, width: 36, height: 36 }}>
                        <SmartToy sx={{ fontSize: 20 }} />
                      </Avatar>
                      <Box sx={{ background: axelionColors.bgWarm, border: `1px solid ${axelionColors.borderLight}`, borderRadius: '8px 8px 8px 4px', p: 2, display: 'flex', gap: 0.5 }}>
                        {[0, 1, 2].map((i) => (
                          <Circle key={i} sx={{ fontSize: 8, color: axelionColors.gold, animation: `${pulse} 1.4s ease-in-out ${i * 0.2}s infinite` }} />
                        ))}
                      </Box>
                    </Box>
                  )}

                  <div ref={messagesEndRef} />
                </>
              )}
            </Box>

            {/* Attached files preview */}
            {attachedFiles.length > 0 && (
              <Box sx={{ px: 2, pt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', borderTop: `1px solid ${axelionColors.borderLight}` }}>
                {attachedFiles.map((file, i) => (
                  <Chip
                    key={i}
                    label={file.name}
                    size="small"
                    icon={file.type.startsWith('image/') ? <ImageIcon sx={{ fontSize: 16 }} /> : <AttachFile sx={{ fontSize: 16 }} />}
                    onDelete={() => removeFile(i)}
                    sx={{ bgcolor: axelionColors.accentLight, color: axelionColors.gold, '& .MuiChip-deleteIcon': { color: axelionColors.gold } }}
                  />
                ))}
              </Box>
            )}

            {/* Input Area */}
            <Box sx={{ p: { xs: 1.5, md: 2 }, borderTop: `1px solid ${axelionColors.borderLight}`, background: axelionColors.bgWarm }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />

                <Tooltip title="Прикрепить файл">
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ color: axelionColors.textMuted, '&:hover': { color: axelionColors.gold } }}
                  >
                    <AttachFile />
                  </IconButton>
                </Tooltip>

                <TextField
                  fullWidth multiline maxRows={4}
                  placeholder="Введите ваш юридический вопрос..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: axelionColors.bgLight, borderRadius: '8px',
                      color: axelionColors.textDark, border: `1px solid ${axelionColors.borderLight}`,
                      '& fieldset': { border: 'none' },
                      '&:hover': { border: `1px solid ${axelionColors.goldMuted}` },
                      '&.Mui-focused': { border: `1px solid ${axelionColors.gold}`, boxShadow: `0 0 0 3px ${axelionColors.gold}15` },
                    },
                    '& .MuiInputBase-input': { color: axelionColors.textDark, '&::placeholder': { color: axelionColors.textMuted, opacity: 1 } },
                  }}
                />

                <Tooltip title="Отправить">
                  <span>
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={(!inputMessage.trim() && attachedFiles.length === 0) || isLoading}
                      sx={{
                        background: axelionColors.gold, color: '#FFFFFF', width: 48, height: 48,
                        '&:hover': { background: axelionColors.goldDark, transform: 'scale(1.05)' },
                        '&.Mui-disabled': { background: axelionColors.bgBeige, color: axelionColors.textMuted },
                        transition: 'all 0.2s',
                      }}
                    >
                      <Send />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Card>

          {/* Right: Lawyers panel - Desktop only */}
          {!isMobile && !isTablet && lawyersPanelOpen && matchedLawyers.length > 0 && (
            <Card sx={{
              width: 320, minWidth: 300, flexShrink: 0, display: 'flex', flexDirection: 'column',
              background: axelionColors.bgLight, border: `1px solid ${axelionColors.borderLight}`,
              boxShadow: '0 4px 16px rgba(26,26,26,0.06)', borderRadius: '8px', overflow: 'hidden',
              animation: `${fadeInUp} 0.3s ease-out`,
            }}>
              <LawyersPanel onClose={() => setLawyersPanelOpen(false)} />
            </Card>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default AIChatPageGlass;
